/*
 * Copyright (c) 2026. Amazon.com, Inc. or its affiliates. All rights reserved.
 */

import { externalData, type ObservableValue } from '@amazon/vinyl-observable'

/**
 * Boot-safe bridge for the child-profile podcasts/audiobooks nav gate. The gate
 * lives in `childProfile`, which carries the profile-type probe and is kept off
 * the first-paint chunk — a static import from the header (which IS on that
 * chunk) would drag the probe back onto boot. This lazy bridge imports and
 * subscribes `childProfile` only when a header nav actually subscribes, so the
 * boot path never pulls it in. Fails SAFE (nav hidden) until the profile
 * resolves to a confirmed non-child.
 */
export const canAccessPodcastsAndAudiobooks$: ObservableValue<boolean> = externalData<boolean>(
    false,
    (setData) => {
        let dispose: (() => void) | undefined
        import('@/auth/childProfile')
            .then((m) => {
                dispose = m.canAccessPodcastsAndAudiobooks$.onData(setData)
            })
            .catch(() => {
                // Chunk load failed — leave the gate fail-safe (nav hidden).
            })
        return () => dispose?.()
    }
)

/**
 * Lazy bridge for the route guard's confirmed-child check — same boot-safe
 * shape as the nav gate above: `childProfile` (which carries the probe) is
 * imported only when a guarded route asks. A failed chunk load resolves
 * `false` (a non-child is never restricted), matching childProfile's fail-open
 * route-guard contract.
 */
export function loadIsChildProfile(): Promise<boolean> {
    return import('@/auth/childProfile').then((m) => m.loadIsChildProfile()).catch(() => false)
}
